import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingList,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    offset,
    type Placement,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useFloatingTree,
    useInteractions,
    useListNavigation,
    useMergeRefs,
    useRole,
    useTypeahead,
} from '@floating-ui/react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import {
    cloneElement,
    type ComponentProps,
    isValidElement,
    type MouseEvent,
    type ReactNode,
    type Ref,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'

import { astraTwMerge } from '../Utilities/astraTwMerge'
import { FLOATING_MOTION_PROPS, TRANSFORM_ORIGIN_BY_PLACEMENT } from '../Utilities/floatingMotion'
import { DropdownMenuContext } from './DropdownMenuContext'
import { type DropdownMenuSearch, DropdownPanelBody } from './DropdownSearch'
import { DROPDOWN_PANEL_CLASS, DROPDOWN_PANEL_PADDING_CLASS, DROPDOWN_SEARCH_PANEL_CLASS } from './dropdownStyles'

const DEFAULT_COLLISION_PADDING = 8
const DEFAULT_SIDE_OFFSET = 4

/**
 * The panel is a motion element, and motion gives these four handlers its own animation and drag
 * signatures. Forwarding the DOM versions would collide, and a menu panel has no use for them.
 */
type PanelDivProps = Omit<ComponentProps<'div'>, 'children' | 'role' | 'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'>

export type AstraDropdownMenuProps = PanelDivProps & {
    /**
     * Content rendered inside the floating dropdown panel, usually AstraDropdownItem items.
     */
    dropdownContent: ReactNode
    /**
     * The trigger element that opens the menu. It must be a valid React element so menu handlers and refs can be attached.
     */
    children: ReactNode
    /**
     * If `true`, the trigger cannot open the menu.
     */
    disabled?: boolean
    /**
     * If `true`, removes the default padding from the dropdown panel.
     */
    noPadding?: boolean
    /**
     * Called whenever the menu requests an open state change. Use with `open` for controlled state.
     */
    onOpenChange?: (open: boolean) => void
    /**
     * Controlled open state. Omit this prop to let the component manage open state internally.
     */
    open?: boolean
    /**
     * Floating UI placement for the menu relative to the trigger, such as `bottom`, `bottom-start`, or `right-end`.
     */
    placement?: Placement
    /**
     * Distance in pixels between the trigger and the menu along the placement axis. Defaults to `4`.
     */
    sideOffset?: number
    /**
     * Offset along the alignment axis, used to nudge start/end aligned menus.
     */
    alignOffset?: number
    /**
     * If `true`, flips and shifts the menu to keep it inside the viewport.
     */
    avoidCollisions?: boolean
    /**
     * Viewport padding used when collision handling is enabled.
     */
    collisionPadding?: number
    /**
     * If `true`, keyboard navigation wraps from the last item to the first item and back.
     */
    loop?: boolean
    /**
     * If `true`, focus is trapped inside the menu while it is open.
     */
    modal?: boolean
    /**
     * Skip the enter and exit animation. Defaults to the viewer's `prefers-reduced-motion` setting, so
     * pass this only to override that — an explicit `false` animates even for a viewer who asked not to.
     */
    reducedMotion?: boolean
    /**
     * Optional portal root for the floating menu. Defaults to the document body.
     */
    container?: HTMLElement | null
    /**
     * A search field pinned above the items, for a list too long to read through. The panel then
     * scrolls, capped at a default height that `className` overrides.
     */
    search?: DropdownMenuSearch
    /**
     * What the panel holds. `menu` is the default and is right for menu items. Pass `dialog` for anything
     * else — a date picker, a form — because a menu may only own menu items, and claiming otherwise is
     * what a screen reader will act on. A panel with a search field is a dialog whatever this says.
     */
    role?: 'menu' | 'dialog'
    /**
     * Keep the panel where it opened, rather than following its trigger. For a trigger that what is chosen
     * in the panel moves — a button at the end of a row of chips, pushed along by every chip added — where
     * following it would slide the panel out from under the pointer mid-selection.
     */
    stayWhereOpened?: boolean
}

type TriggerElementProps = {
    ref?: Ref<HTMLElement>
    disabled?: boolean
    onClick?: (event: MouseEvent<Element>) => void
    [key: string]: unknown
}

const AstraDropdownMenuImpl = ({
    dropdownContent,
    children,
    className,
    disabled,
    noPadding,
    onOpenChange,
    open,
    placement = 'bottom',
    sideOffset = DEFAULT_SIDE_OFFSET,
    alignOffset = 0,
    avoidCollisions = true,
    collisionPadding = DEFAULT_COLLISION_PADDING,
    loop = true,
    modal = false,
    reducedMotion,
    container,
    search,
    role: panelRole = 'menu',
    stayWhereOpened = false,
    ...contentProps
}: AstraDropdownMenuProps) => {
    const isControlled = open !== undefined
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    // Kept apart from the index above, which floating-ui moves the browser's focus to. A searching panel
    // marks a row without focusing it, so the two would fight over where the caret is.
    const [searchActiveIndex, setSearchActiveIndex] = useState<number | null>(null)
    const listItemsRef = useRef<Array<HTMLElement | null>>([])
    const listContentRef = useRef<Array<string | null>>([])
    const searchRef = useRef<HTMLInputElement>(null)
    const itemIdPrefix = useId()
    const openSubmenuRef = useRef<string | null>(null)
    const currentOpen = isControlled ? open : uncontrolledOpen

    const setCurrentOpen = (nextOpen: boolean) => {
        if (!isControlled) setUncontrolledOpen(nextOpen)
        if (!nextOpen) setActiveIndex(null)
        onOpenChange?.(nextOpen)
    }

    const prefersReducedMotion = useReducedMotion()
    const skipMotion = reducedMotion ?? prefersReducedMotion ?? false

    const nodeId = useFloatingNodeId()
    const tree = useFloatingTree()

    // A submenu item closes the whole tree, not just the panel it sits in.
    useEffect(() => {
        if (!tree) return
        const closeEverything = () => setCurrentOpen(false)
        tree.events.on('click', closeEverything)
        return () => tree.events.off('click', closeEverything)
    })

    const {
        x,
        y,
        strategy,
        refs,
        context,
        placement: resolvedPlacement,
    } = useFloating({
        nodeId,
        open: currentOpen,
        onOpenChange: setCurrentOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset({ mainAxis: sideOffset, alignmentAxis: alignOffset }),
            ...(avoidCollisions ? [flip({ padding: collisionPadding }), shift({ padding: collisionPadding })] : []),
        ],
    })

    // The panel is positioned against a snapshot of where the trigger stood on opening, instead of the trigger.
    useLayoutEffect(() => {
        const trigger = refs.domReference.current
        if (!stayWhereOpened || !currentOpen || !trigger) return

        const rect = trigger.getBoundingClientRect()
        refs.setPositionReference({ getBoundingClientRect: () => rect, contextElement: trigger })
        return () => refs.setPositionReference(trigger)
    }, [stayWhereOpened, currentOpen, refs])

    const click = useClick(context, { enabled: !disabled })
    const dismiss = useDismiss(context)
    // A panel holding a search field is a dialog around a menu, not a menu: see DropdownPanelBody.
    const role = useRole(context, { role: search || panelRole === 'dialog' ? 'dialog' : 'menu' })
    const listNavigation = useListNavigation(context, {
        listRef: listItemsRef,
        activeIndex,
        onNavigate: setActiveIndex,
        loop,
        // 'auto' means the first item is focused when the menu is opened from the keyboard and not when it
        // is clicked open: a highlighted row after a click reads as a choice already made. A search field
        // takes the focus instead, so a menu that has one never puts it on an item.
        focusItemOnOpen: search ? false : 'auto',
        // Hovering a row moves focus to it, which in a searching panel would take the caret out of the
        // field mid-word. There the row under the pointer is marked by CSS and nothing else moves.
        focusItemOnHover: !search,
    })
    const typeahead = useTypeahead(context, {
        listRef: listContentRef,
        activeIndex,
        onMatch: index => setActiveIndex(index),
    })
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, role, listNavigation, typeahead])

    // Preserve any ref the consumer attached to the trigger; cloneElement would otherwise drop it in favor of setReference.
    const triggerRef = isValidElement<TriggerElementProps>(children) ? children.props.ref : undefined
    const setTriggerRef = useMergeRefs<HTMLElement>([refs.setReference, triggerRef])

    if (!isValidElement<TriggerElementProps>(children)) return <>{children}</>

    const triggerProps = children.props
    const referenceProps = getReferenceProps({
        ...triggerProps,
        ...(disabled ? { 'aria-disabled': true } : {}),
        onClick: event => {
            if (disabled) {
                event.preventDefault()
                return
            }
            triggerProps.onClick?.(event)
        },
    })

    return (
        <FloatingNode id={nodeId}>
            {cloneElement(children, {
                ...referenceProps,
                disabled: triggerProps.disabled || disabled,
                ref: setTriggerRef,
            })}
            <AnimatePresence>
                {currentOpen && (
                    <FloatingPortal root={container}>
                        <FloatingFocusManager context={context} modal={modal} initialFocus={search ? searchRef : -1}>
                            <motion.div
                                {...contentProps}
                                {...(skipMotion ? {} : FLOATING_MOTION_PROPS)}
                                {...getFloatingProps({
                                    ref: refs.setFloating,
                                    style: {
                                        ...contentProps.style,
                                        position: strategy,
                                        top: y,
                                        left: x,
                                    },
                                    className: astraTwMerge(
                                        DROPDOWN_PANEL_CLASS,
                                        TRANSFORM_ORIGIN_BY_PLACEMENT[resolvedPlacement],
                                        search ? DROPDOWN_SEARCH_PANEL_CLASS : noPadding ? '' : DROPDOWN_PANEL_PADDING_CLASS,
                                        className
                                    ),
                                })}
                            >
                                <DropdownMenuContext
                                    value={{
                                        getItemProps: userProps => ({
                                            ...getItemProps(userProps),
                                        }),
                                        closeMenu: () => setCurrentOpen(false),
                                        skipMotion,
                                        activeIndex: search ? searchActiveIndex : null,
                                        setActiveIndex: search ? setSearchActiveIndex : undefined,
                                        itemIdPrefix,
                                        openSubmenuRef,
                                    }}
                                >
                                    <FloatingList elementsRef={listItemsRef} labelsRef={listContentRef}>
                                        <DropdownPanelBody
                                            search={search}
                                            searchRef={searchRef}
                                            navigation={{ itemsRef: listItemsRef, activeIndex: searchActiveIndex, setActiveIndex: setSearchActiveIndex }}
                                            itemIdPrefix={itemIdPrefix}
                                            noPadding={noPadding}
                                        >
                                            {dropdownContent}
                                        </DropdownPanelBody>
                                    </FloatingList>
                                </DropdownMenuContext>
                            </motion.div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </AnimatePresence>
        </FloatingNode>
    )
}

/**
 * Opens a list of actions from a trigger. Submenus coordinate through a FloatingTree, so the root menu
 * provides one; a menu rendered inside another menu joins the tree already there rather than starting
 * a second one.
 */
export const AstraDropdownMenu = (props: AstraDropdownMenuProps) => {
    const parentId = useFloatingParentNodeId()

    if (parentId === null) {
        return (
            <FloatingTree>
                <AstraDropdownMenuImpl {...props} />
            </FloatingTree>
        )
    }

    return <AstraDropdownMenuImpl {...props} />
}
